import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { APP_META } from '../constants/themes';
import { useAuth } from '../../auth/AuthContext';
import TeamSelector from '../../team/TeamSelector';

/**
 * Top navigation bar with app switcher, team links, and user menu.
 * Hidden during active exams.
 */
export default function AppNav() {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const { isAuthenticated, isAdmin, displayName, isLocalMode } = useAuth();

  if (location.pathname.startsWith('/exam/active')) return null;

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const isDark = location.pathname.startsWith('/flashcards') ||
    location.pathname.startsWith('/drill') ||
    location.pathname.startsWith('/progress') ||
    location.pathname.startsWith('/leaderboard') ||
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/profile') ||
    location.pathname === '/';

  const navLinkClass = (path) => `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
    isActive(path)
      ? isDark ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'
      : isDark ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
  }`;

  return (
    <nav className={`sticky top-0 z-40 border-b ${
      isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
    }`}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 font-bold text-lg shrink-0">
            <span className="text-blue-500">CCNP</span>
            <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Trainer</span>
            <span style={{ fontFamily: "'Caveat', cursive", color: '#049fd9' }} className="ml-2 text-xl font-bold">by SEs for SEs</span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {APP_META.map((app) => (
              <Link key={app.id} to={app.path} className={navLinkClass(app.path)}>
                <span className="mr-1.5">{app.icon}</span>
                {app.shortName}
              </Link>
            ))}
            <Link to="/progress" className={navLinkClass('/progress')}>
              <span className="mr-1.5">📊</span>Progress
            </Link>
            {!isLocalMode && (
              <TeamSelector isDark={isDark} />
            )}
          </div>

          {/* User menu (desktop) */}
          <div className="hidden md:flex items-center gap-2">
            {isAdmin && (
              <Link to="/admin" className={`text-xs px-2 py-1 rounded ${
                isDark ? 'text-purple-400 hover:bg-gray-800' : 'text-purple-600 hover:bg-gray-100'
              }`}>
                Admin
              </Link>
            )}
            {isAuthenticated ? (
              <Link to="/profile" className={`text-sm ${
                isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
              }`}>
                {displayName}
              </Link>
            ) : !isLocalMode ? (
              <Link to="/login" className="text-sm text-blue-400 hover:text-blue-300">
                Sign In
              </Link>
            ) : null}
          </div>

          {/* Mobile menu button */}
          <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden p-2 rounded-lg">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {menuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden pb-4 space-y-1">
            {APP_META.map((app) => (
              <Link key={app.id} to={app.path} onClick={() => setMenuOpen(false)}
                className={`block px-3 py-2 rounded-lg text-sm font-medium ${
                  isActive(app.path) ? (isDark ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900')
                    : (isDark ? 'text-gray-400' : 'text-gray-500')
                }`}>
                <span className="mr-2">{app.icon}</span>{app.name}
              </Link>
            ))}
            <Link to="/progress" onClick={() => setMenuOpen(false)}
              className={`block px-3 py-2 rounded-lg text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              📊 Progress
            </Link>
            {!isLocalMode && (
              <>
                <Link to="/leaderboard" onClick={() => setMenuOpen(false)}
                  className={`block px-3 py-2 rounded-lg text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  🏆 Team Leaderboard
                </Link>
                <Link to="/teams" onClick={() => setMenuOpen(false)}
                  className={`block px-3 py-2 rounded-lg text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Manage Teams
                </Link>
                {isAdmin && (
                  <Link to="/admin" onClick={() => setMenuOpen(false)}
                    className="block px-3 py-2 rounded-lg text-sm font-medium text-purple-400">
                    Admin Dashboard
                  </Link>
                )}
              </>
            )}
            <div className="border-t border-gray-700 mt-2 pt-2">
              {isAuthenticated ? (
                <Link to="/profile" onClick={() => setMenuOpen(false)}
                  className={`block px-3 py-2 rounded-lg text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {displayName} — Profile
                </Link>
              ) : !isLocalMode ? (
                <Link to="/login" onClick={() => setMenuOpen(false)}
                  className="block px-3 py-2 rounded-lg text-sm text-blue-400">
                  Sign In
                </Link>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
